import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import {
  Box,
  Checkbox,
  TextField,
  Typography
} from "@mui/material";
import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from "react";
import { useSuperAdminSingleSuscrip } from "../../../context/SuperAdmin/SingleSuscriptContext";
import { iDataUser } from "../../../interfaces/SuperAdmin/Pages/iDataUser";

interface Props {
  selectedUsers: string[];
  setSelectedUsers: Dispatch<SetStateAction<string[]>>;
}

const OtherUsersMenu: FC<Props> = ({ selectedUsers, setSelectedUsers }) => {
  const { allUsers, suscribedUsers, isAdding, plan } =
    useSuperAdminSingleSuscrip();

  const [missingUsers, setMissingUsers] = useState<iDataUser[]>([]);
  const [maxUsers, setMaxUsers] = useState(0);

  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    // Hacemos un filtro de todos los usuarios. Evaluamos si alguno de los usuarios ya estan en la suscripcion.
    //De esta forma dejamos solo los usuarios que no han sido elegidos
    const newArray = allUsers.filter(
      (user) =>
        !suscribedUsers.some((item) => item.id_usuario === user.id_usuario)
    );
    setMissingUsers(newArray);
  }, [allUsers, suscribedUsers]);

  // Función para manejar la selección/deselección individual
  const handleCheckboxChange = (id: string) => {
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((comp) => comp !== id) : [...prev, id]
    );
  };

  // Función para seleccionar o deseleccionar todos
  const toggleSelectAll = () => {
    if (selectedUsers.length > 0) {
      setSelectedUsers([]); // Si todos están seleccionados, deselecciona
    } else {
      setSelectedUsers(missingUsers.map((user) => user.id_usuario)); // Selecciona todos
    }
  };

  useEffect(() => {
    setMaxUsers(
      plan.max_usuarios - suscribedUsers.length - selectedUsers.length
    );
  }, [selectedUsers, plan.max_usuarios]);

  // Filtro de usuarios por búsqueda
  const filteredUsers = useMemo(() => {
    const normalizedSearch = searchText.toLowerCase().trim();
    return missingUsers.filter(
      (u) =>
        `${u.nombres} ${u.apellidos}`
          .toLowerCase()
          .includes(normalizedSearch) ||
        u.email.toLowerCase().includes(normalizedSearch)
    );
  }, [searchText, missingUsers]);

  return (
    <>
      <Box
        sx={{
          width: "100%",
          border: "1px solid rgba(51, 107, 135, 0.5)", // Borde con opacidad
          borderRadius: "8px", // Bordes redondeados
          padding: "8px 16px", // Padding general
          transition: "all 0.3s ease", // Suaviza la transición
        }}
      >
        {/* Título */}
        <Typography variant="h6" component="h2" color="#000">
          Otros usuarios{" "}
          <Typography component="span" color={"#73767E"} fontSize={"20px"}>
            {selectedUsers.length > 0
              ? `(${selectedUsers.length}/${missingUsers.length})`
              : `(${missingUsers.length})`}
          </Typography>
        </Typography>

        {/* Search bar */}
        <TextField
          fullWidth
          placeholder="Buscar por nombre o email"
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{
            marginTop: "8px",
            backgroundColor: "#F9F9F9",
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
        />

        {/* Botón de Seleccionar/Deseleccionar todos */}
        {missingUsers.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: "8px",
              py: "14px",
              marginTop: "8px",
              borderRadius: "6px",
              fontFamily: "Montserrat",
              fontWeight: "bold",
              backgroundColor: "#336B87",
              color: "#FFF",
            }}
          >
            <Box
              sx={{
                width: "40px",
                display: "flex",
                justifyContent: "center",
                mr: "8px",
              }}
            >
              <Checkbox
                checked={
                  selectedUsers.length === missingUsers.length &&
                  missingUsers.length > 0
                }
                indeterminate={
                  selectedUsers.length > 0 &&
                  selectedUsers.length < missingUsers.length
                }
                onChange={toggleSelectAll}
                disabled={
                  isAdding ||
                  (missingUsers.length > maxUsers && !selectedUsers.length)
                }
                icon={<CheckBoxOutlineBlankIcon sx={{ color: "#FFF" }} />}
                checkedIcon={<CheckBoxIcon sx={{ color: "#FFF" }} />}
                indeterminateIcon={
                  <IndeterminateCheckBoxIcon sx={{ color: "#FFF" }} />
                }
                sx={{
                  padding: 0,
                }}
              />
            </Box>
            <Typography sx={{ flex: 1 }}>Nombre</Typography>
            <Typography sx={{ flex: 1 }}>Email</Typography>
          </Box>
        )}

        {/* Contenido con scroll si excede los 350px */}
        <Box
          sx={{
            marginTop: "8px",
            paddingTop: "8px",
            borderTop: "1px solid rgba(51, 107, 135, 0.5)", // Separador sutil
            maxHeight: "350px", // Máximo de altura
            overflowY: "auto", // Scroll vertical si es necesario
          }}
        >
          {/* Lista de usuarios */}
          {filteredUsers.length > 0 ? (
            filteredUsers.map((usuario) => (
              <Box
                key={usuario.id_usuario}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "4px 8px",
                }}
              >
                <Checkbox
                  checked={selectedUsers.includes(usuario.id_usuario)}
                  disabled={
                    isAdding ||
                    (!maxUsers && !selectedUsers.includes(usuario.id_usuario))
                  }
                  onChange={() => handleCheckboxChange(usuario.id_usuario)}
                  sx={{
                    color: "#336B87",
                    "&.Mui-checked": { color: "#336B87" },
                    marginRight: "8px",
                  }}
                />
                <Typography sx={{ flex: 1, color: "#000" }}>
                  {usuario.nombres} {usuario.apellidos}
                </Typography>
                <Typography sx={{ flex: 1, color: "#73767E" }}>
                  {usuario.email}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography color="#73767E" fontStyle="italic" textAlign="center">
              No hay más usuarios disponibles
            </Typography>
          )}
        </Box>
      </Box>
      <Typography component={"span"} sx={{ fontSize: 18, fontWeight: 500 }}>
        Usuarios restantes: {maxUsers}
      </Typography>
    </>
  );
};

export default OtherUsersMenu;
